import { RawTexture2DArray as BABYLON_RawTexture2DArray } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/rawTexture2DArray.js';
import { IVector3 } from '../api.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
export class LutEffect {
    constructor(viewer) {
        this._lut_texture = null;
        this._viewer = viewer;
    }
    /**
     * Load a .cube file
     * @param data The content of the cube file
     */
    load_cube_file_from_memory(data) {
        var _a;
        (_a = this._lut_texture) === null || _a === void 0 ? void 0 : _a.dispose();
        const lines = data.split('\n');
        let lut_size = 0;
        let is_3d = false;
        const CUBE_LUT_3D_FIELD_NAME = 'LUT_3D_SIZE';
        const CUBE_LUT_1D_FIELD_NAME = 'LUT_1D_SIZE';
        const lut_data = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            // Skip empty lines and comments
            if (line.length === 0 || line.substring(0, 1) === '#')
                continue;
            if (line.startsWith(CUBE_LUT_3D_FIELD_NAME)) {
                is_3d = true;
                lut_size = Number.parseInt(line.substring(CUBE_LUT_3D_FIELD_NAME.length + 1));
            }
            else if (line.startsWith(CUBE_LUT_1D_FIELD_NAME)) {
                is_3d = false;
                lut_size = Number.parseInt(line.substring(CUBE_LUT_3D_FIELD_NAME.length + 1));
            }
            else {
                const words = line.split(' ');
                if (words.length === 3) {
                    const color = new IVector3(Number.parseFloat(words[0]), Number.parseFloat(words[1]), Number.parseFloat(words[2]));
                    lut_data.push(color);
                }
            }
        }
        if (!is_3d) {
            throw new Error('1-Dimensional CUBE files are not supported, please use a 3D cube file.');
        }
        const expected_lut_data_size = is_3d ? (lut_size * lut_size * lut_size) : lut_size;
        if (lut_data.length !== expected_lut_data_size) {
            throw new Error('Invalid CUBE file: Expected ' + expected_lut_data_size + ' data entries, but got ' + lut_data.length);
        }
        const packed_data = new Float32Array(lut_data.length * 4);
        for (let i = 0; i < lut_data.length; i++) {
            packed_data[4 * i + 0] = lut_data[i].x;
            packed_data[4 * i + 1] = lut_data[i].y;
            packed_data[4 * i + 2] = lut_data[i].z;
            packed_data[4 * i + 3] = 1.0;
        }
        this._lut_texture = new BABYLON_RawTexture2DArray(packed_data, lut_size, lut_size, lut_size, BABYLON_Constants.TEXTUREFORMAT_RGBA, this._viewer.scene, false, false, BABYLON_Constants.TEXTURE_BILINEAR_SAMPLINGMODE, BABYLON_Constants.TEXTURETYPE_FLOAT);
        this._lut_texture.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._lut_texture.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._lut_texture.wrapR = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._viewer.mark_scene_dirty();
    }
    load_cube_file_from_url(url) {
        return new Promise((resolve, reject) => {
            fetch(new Request(url)).then((response) => {
                if (response.ok) {
                    response.text().then((data) => {
                        this.load_cube_file_from_memory(data);
                        resolve();
                    }, reject);
                }
                else {
                    reject(new Error('Bad response: ' + response.statusText));
                }
            }, reject);
        });
    }
    load_lut_texture_from_url(url) {
        let texture_source = null;
        return new Promise((resolve, reject) => {
            texture_source = new BABYLON_Texture(url, this._viewer.scene, true, false, undefined, () => {
                var _a;
                const source = texture_source;
                (_a = source.readPixels()) === null || _a === void 0 ? void 0 : _a.then((data) => {
                    const source_width = source.getSize().width;
                    const source_height = source.getSize().height;
                    if (source_height * source_height !== source_width) {
                        reject(new Error('Invalid LUT texture dimensions'));
                        return;
                    }
                    if (source.textureType !== BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE) {
                        reject(new Error('Invalid LUT texture format'));
                        return;
                    }
                    if (source.textureFormat !== BABYLON_Constants.TEXTUREFORMAT_RGBA && source.textureFormat !== BABYLON_Constants.TEXTUREFORMAT_RGB) {
                        reject(new Error('Invalid LUT texture channel count'));
                        return;
                    }
                    const source_channels = source.textureFormat === BABYLON_Constants.TEXTUREFORMAT_RGBA ? 4 : 3;
                    const lut_size = source_height;
                    const pixels = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
                    const destination_data = new Uint8Array(source_width * source_height * 4);
                    for (let slice = 0; slice < lut_size; slice++) {
                        for (let y = 0; y < lut_size; y++) {
                            for (let x = 0; x < lut_size; x++) {
                                const src_x = x + slice * lut_size;
                                const src_index = src_x + source_width * y;
                                const dst_index = slice * lut_size * lut_size + y * lut_size + x;
                                destination_data[4 * dst_index + 0] = pixels[source_channels * src_index + 0];
                                destination_data[4 * dst_index + 1] = pixels[source_channels * src_index + 1];
                                destination_data[4 * dst_index + 2] = pixels[source_channels * src_index + 2];
                                destination_data[4 * dst_index + 3] = 255;
                            }
                        }
                    }
                    this._lut_texture = new BABYLON_RawTexture2DArray(destination_data, lut_size, lut_size, lut_size, BABYLON_Constants.TEXTUREFORMAT_RGBA, this._viewer.scene, false, false, BABYLON_Constants.TEXTURE_BILINEAR_SAMPLINGMODE, BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE);
                    this._lut_texture.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
                    this._lut_texture.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
                    this._lut_texture.wrapR = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
                    this._viewer.mark_scene_dirty();
                    resolve();
                }).catch(reject);
            }, (msg) => {
                reject(new Error(msg));
            });
        }).finally(() => {
            texture_source === null || texture_source === void 0 ? void 0 : texture_source.dispose();
        });
    }
    unload_lut() {
        var _a;
        if (this._lut_texture !== null) {
            (_a = this._lut_texture) === null || _a === void 0 ? void 0 : _a.dispose();
            this._lut_texture = null;
            this._viewer.mark_scene_dirty();
        }
    }
    get lut_texture() {
        return this._lut_texture;
    }
    dispose() {
        this.unload_lut();
    }
    static is_supported(engine) {
        if (engine._webGLVersion === 2) {
            return engine.getCaps().textureFloat && engine.getCaps().textureFloatLinearFiltering;
        }
        else {
            return false;
        }
    }
}
//# sourceMappingURL=LutEffect.js.map